import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { ProductDialog, PackageDialog } from "@/components/catalogue/CatalogueDialogs";
import { PlanBadges } from "@/components/catalogue/EventPanel";

export default function ProductPanel({ product, onClose, onChange, skus, canEdit, canDelete }) {
  const [editOpen, setEditOpen] = useState(false);
  const [pkgDialog, setPkgDialog] = useState(null); // { pkg? } — open when set
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!product?.id) { setHistory([]); return; }
    setLoading(true);
    api.get(`/catalogue/products/${product.id}/event-history`)
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [product?.id]);

  if (!product) return null;

  const deletePackage = async (pkg) => {
    if (!window.confirm(`Delete package ${pkg.code}? Only allowed if it isn't linked to any transactions.`)) return;
    try {
      await api.delete(`/catalogue/skus/${pkg.id}`);
      toast.success("Package deleted");
      onChange?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "This item is linked to existing transactions and cannot be deleted.");
    }
  };

  const deleteProduct = async () => {
    if (!window.confirm(`Delete product ${product.code}? Only allowed if it has no packages or transactions.`)) return;
    try {
      await api.delete(`/catalogue/products/${product.id}`);
      toast.success("Product deleted");
      onChange?.();
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "This item is linked to existing transactions and cannot be deleted.");
    }
  };

  return (
    <Sheet open={!!product} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="product-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{product.code}</span>
            <span>{product.name}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <section className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Product details</div>
              <div className="flex gap-1">
                {canEdit && <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)} data-testid="product-panel-edit"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>}
                {canDelete && <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={deleteProduct} data-testid="product-panel-delete"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>}
              </div>
            </div>
            <div className="text-sm text-slate-700">{product.description || <span className="text-slate-400 italic">No description</span>}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-2 capitalize">Family: {product.family || "—"}</div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800">Packages under this product</div>
              {canEdit && <Button size="sm" onClick={() => setPkgDialog({})} data-testid="add-package-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add package</Button>}
            </div>
            <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="w-32">Code</TableHead>
                    <TableHead>Name / Description</TableHead>
                    <TableHead className="w-40 text-right">Price (MYR)</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {skus.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">No packages yet.</TableCell></TableRow>}
                  {skus.map((s) => (
                    <TableRow key={s.id} data-testid={`package-row-${s.code}`}>
                      <TableCell className="font-mono text-xs">{s.code}</TableCell>
                      <TableCell>
                        <div className="text-sm">{s.name}</div>
                        {s.default_description && <div className="text-xs text-slate-500">{s.default_description}</div>}
                      </TableCell>
                      <TableCell className="text-right">
                        {(s.default_price_minor || 0) > 0 ? (
                          <span className="font-mono text-sm tabular-nums">{fmtMoney(s.default_price_minor, "MYR")}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5" data-testid={`package-nprice-${s.code}`}>
                            No price set
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-0.5">
                          {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPkgDialog({ pkg: s })} data-testid={`package-edit-${s.code}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                          {canDelete && <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-rose-50" onClick={() => deletePackage(s)} data-testid={`package-delete-${s.code}`}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          <section>
            <div className="text-sm font-semibold text-slate-800 mb-2">Event history</div>
            <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead className="w-28">Package</TableHead>
                    <TableHead className="w-32 text-right">Price</TableHead>
                    <TableHead>Allowed plans</TableHead>
                    <TableHead className="w-28">Start date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-4">Loading…</TableCell></TableRow>}
                  {!loading && history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-4">Not offered in any event yet.</TableCell></TableRow>}
                  {!loading && history.map((h) => (
                    <TableRow key={h.offer_id} data-testid={`history-row-${h.offer_id}`}>
                      <TableCell>
                        <div className="text-sm">{h.event_name}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{h.event_code}</div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{h.sku_code}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(h.price_minor, h.currency)}</TableCell>
                      <TableCell><PlanBadges plans={h.allowed_plans} /></TableCell>
                      <TableCell className="text-xs font-mono text-slate-600">{h.start_date || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        </div>

        <ProductDialog open={editOpen} onOpenChange={setEditOpen} product={product} onDone={() => { setEditOpen(false); onChange?.(); toast.success("Product updated"); }} />
        <PackageDialog
          open={!!pkgDialog}
          onOpenChange={(v) => { if (!v) setPkgDialog(null); }}
          pkg={pkgDialog?.pkg}
          product={product}
          onDone={() => { setPkgDialog(null); onChange?.(); toast.success(pkgDialog?.pkg ? "Package updated" : "Package created"); }}
        />
      </SheetContent>
    </Sheet>
  );
}
